// Das Geschlecht wird aus der Anrede abgeleitet, nicht mehr getrennt erfasst.
//
// Warum das gefahrlos ist: `Contact.gender` wird nirgends für Briefe,
// Serienbriefe, Mailvorlagen, PDF-Export oder Statistik gelesen — die
// automatischen Mail-Anreden benutzen bewusst nur „ist Firma" und den
// Vornamen. Der einzige Verbraucher ist die Verwandtschafts- und
// Haushaltsableitung („Schwager", „Ehefrau"), und die kommt über `genderOf`
// (utils/householdRole.ts) ohnehin schon an die Anrede heran.
//
// Warum die Funktion trotzdem gebraucht wird: Das Feld bleibt in der Datenbank
// und wird von der Ableitung, der Datenbereinigung und dem Import gelesen. Es
// soll gefüllt sein — nur eben von selbst.
//
// ACHTUNG, NAMENSFALLE: Das Formularfeld `anrede` bedeutet an zwei Stellen
// Verschiedenes. In der Kontaktakte trägt es die Katalog-ID der Anrede, in der
// Privat-Anlage trug es bis 08/2026 den Geschlechtstext. Diese Funktion nimmt
// immer das LABEL („Herr", „Frau"), nie eine ID.

/** Die vier Werte, die `Contact.gender` fachlich annehmen soll. */
export const GESCHLECHT_WERTE = ["Männlich", "Weiblich", "Divers", "Ohne Angabe"] as const;
export type Geschlecht = (typeof GESCHLECHT_WERTE)[number];

/**
 * Geschlecht zu einer Anrede, oder `undefined`, wenn die Anrede nichts
 * darüber aussagt (leer, „Firma", „Eheleute", ein selbst gepflegter Katalogwert).
 *
 * `Keine Angabe` — so heißt der Wert im Anrede-Katalog — wird auf `Ohne Angabe`
 * abgebildet, die Schreibweise der Geschlechtsspalte. Die beiden Kataloge sind
 * historisch unterschiedlich benannt.
 */
export function geschlechtAusAnrede(anrede: string | null | undefined): Geschlecht | undefined {
  const a = String(anrede ?? "").trim().toLowerCase();
  if (!a) return undefined;
  if (a === "herr" || a === "herrn") return "Männlich";
  if (a === "frau") return "Weiblich";
  if (a === "divers") return "Divers";
  if (a === "keine angabe" || a === "ohne angabe") return "Ohne Angabe";
  return undefined;
}

/**
 * Das Geschlecht, das gespeichert werden soll: das abgeleitete, wenn die
 * Anrede etwas hergibt — sonst der bisherige Wert.
 *
 * Der bisherige Wert gewinnt NIE gegen eine aussagekräftige Anrede: Wer die
 * Anrede von „Herr" auf „Frau" ändert, hat sich korrigiert, und ein
 * stehengebliebenes „Männlich" wäre der Fehler, den diese Umstellung beseitigen
 * soll. Umgekehrt bleibt ein von Hand gepflegter Wert erhalten, solange die
 * Anrede schweigt.
 */
export function geschlechtFuerSpeichern(
  anrede: string | null | undefined,
  bisher: string | null | undefined,
): string | null | undefined {
  return geschlechtAusAnrede(anrede) ?? bisher;
}
